// API service for Mangu CRM

const API_BASE_URL = window.location.hostname === 'localhost'
    ? 'http://localhost:5000/api'
    : '/api';

// Auth tokens. The browser holds the JWTs in httpOnly cookies (the server
// sets them on login). The legacy localStorage tokens are kept as a
// compatibility fallback so existing sessions keep working during rollout;
// new logins do not need them, but we still read them if present.
function getAuthToken() {
    return localStorage.getItem('access_token');
}

// Read the CSRF token cookie set by flask-jwt-extended. JS can read it
// (it's intentionally not httpOnly); we echo it as a header on mutating
// requests to satisfy the CSRF check on cookie-authenticated calls.
function getCsrfToken(name = 'csrf_access_token') {
    const m = document.cookie.match(new RegExp('(?:^|;\\s*)' + name + '=([^;]+)'));
    return m ? decodeURIComponent(m[1]) : null;
}

let refreshInflight = null;

async function tryRefreshToken() {
    if (refreshInflight) return refreshInflight;

    refreshInflight = (async () => {
        try {
            const csrf = getCsrfToken('csrf_refresh_token');
            const headers = { 'Content-Type': 'application/json' };
            if (csrf) headers['X-CSRF-TOKEN'] = csrf;
            // Legacy: also send refresh token from localStorage if we have one
            const rt = localStorage.getItem('refresh_token');
            if (rt) headers['Authorization'] = `Bearer ${rt}`;

            const resp = await fetch(`${API_BASE_URL}/auth/refresh`, {
                method: 'POST',
                credentials: 'include',
                headers,
            });
            if (!resp.ok) return null;
            const body = await resp.json();
            // New tokens land in cookies automatically; we still update
            // localStorage if the legacy body fields are present.
            if (body.access_token) localStorage.setItem('access_token', body.access_token);
            if (body.user) localStorage.setItem('user', JSON.stringify(body.user));
            return body.access_token || 'cookie';
        } catch {
            return null;
        } finally {
            refreshInflight = null;
        }
    })();

    return refreshInflight;
}

function logoutLocal(redirect = true) {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
    if (redirect) window.location.href = '/';
}

const _MUTATING = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

// Authenticated file download (apiFetch always JSON-parses; this streams a blob)
async function apiDownload(endpoint, filename) {
    const resp = await fetch(`${API_BASE_URL}${endpoint}`, {
        headers: { Authorization: `Bearer ${getAuthToken()}` }
    });
    if (!resp.ok) {
        const body = await resp.json().catch(() => ({}));
        throw new Error(body.error || 'La descarga falló');
    }
    const blob = await resp.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
}

// Common fetch function with auth + automatic refresh on 401
async function apiFetch(endpoint, options = {}, _retry = false) {
    const token = getAuthToken();
    const method = (options.method || 'GET').toUpperCase();
    const headers = {
        'Content-Type': 'application/json',
        ...options.headers,
    };

    if (token) {
        headers['Authorization'] = `Bearer ${token}`;
    }
    // CSRF: required for cookie-auth on mutating requests; harmless otherwise
    if (_MUTATING.has(method)) {
        const csrf = getCsrfToken();
        if (csrf) headers['X-CSRF-TOKEN'] = csrf;
    }

    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        ...options,
        credentials: 'include',
        headers,
    });

    if (response.status === 401) {
        // Skip refresh if this *is* the refresh call or we already retried
        if (!_retry && endpoint !== '/auth/login' && endpoint !== '/auth/refresh') {
            const newAccess = await tryRefreshToken();
            if (newAccess) return apiFetch(endpoint, options, true);
        }
        logoutLocal();
        throw new Error('Sesión expirada. Inicia sesión nuevamente.');
    }

    if (response.status === 429) {
        let body = {};
        try { body = await response.json(); } catch {}
        throw new Error(body.error || 'Demasiadas solicitudes. Intenta más tarde.');
    }

    const data = await response.json().catch(() => ({}));

    if (!response.ok) {
        throw new Error(data.error || `API request failed (${response.status})`);
    }

    return data;
}

// Generic API helper
export const api = {
    get: (endpoint) => apiFetch(endpoint),
    post: (endpoint, data) => apiFetch(endpoint, { method: 'POST', body: JSON.stringify(data) }),
    put: (endpoint, data) => apiFetch(endpoint, { method: 'PUT', body: JSON.stringify(data) }),
    delete: (endpoint) => apiFetch(endpoint, { method: 'DELETE' })
};

// File download helper: streams the response as a Blob and triggers a browser
// download. Used for /api/export endpoints; falls back to fallbackFilename if
// the server doesn't send Content-Disposition.
export const exportAPI = {
    download: async (path, fallbackFilename) => {
        const fetchOnce = async (token) => {
            const headers = {};
            if (token) headers['Authorization'] = `Bearer ${token}`;
            return fetch(`${API_BASE_URL}${path}`, {
                credentials: 'include',
                headers,
            });
        };

        let token = getAuthToken();
        let resp = await fetchOnce(token);
        if (resp.status === 401) {
            const newToken = await tryRefreshToken();
            if (newToken) resp = await fetchOnce(newToken);
        }
        if (!resp.ok) {
            let body = {};
            try { body = await resp.json(); } catch {}
            throw new Error(body.error || `Descarga falló (${resp.status})`);
        }

        let filename = fallbackFilename;
        const cd = resp.headers.get('Content-Disposition') || '';
        const m = cd.match(/filename="?([^"]+)"?/);
        if (m) filename = m[1];

        const blob = await resp.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 1000);
    },
};

// Auth API
export const authAPI = {
    login: async (email, password) => {
        const data = await apiFetch('/auth/login', {
            method: 'POST',
            body: JSON.stringify({ email, password }),
        });
        localStorage.setItem('access_token', data.access_token);
        if (data.refresh_token) localStorage.setItem('refresh_token', data.refresh_token);
        localStorage.setItem('user', JSON.stringify(data.user));
        return data;
    },

    register: async (name, email, password, company = '') => {
        return apiFetch('/auth/register', {
            method: 'POST',
            body: JSON.stringify({ name, email, password, company }),
        });
    },

    forgotPassword: (email) =>
        apiFetch('/auth/forgot-password', {
            method: 'POST', body: JSON.stringify({ email })
        }),
    resetPassword: (token, password) =>
        apiFetch('/auth/reset-password', {
            method: 'POST', body: JSON.stringify({ token, password })
        }),
    verifyEmail: (token) =>
        apiFetch('/auth/verify-email', {
            method: 'POST', body: JSON.stringify({ token })
        }),
    resendVerification: () =>
        apiFetch('/auth/resend-verification', { method: 'POST' }),

    logout: async () => {
        try {
            const csrf = getCsrfToken();
            const headers = {};
            if (getAuthToken()) headers['Authorization'] = `Bearer ${getAuthToken()}`;
            if (csrf) headers['X-CSRF-TOKEN'] = csrf;
            await fetch(`${API_BASE_URL}/auth/logout`, {
                method: 'POST',
                credentials: 'include',
                headers,
            });
        } catch {}
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('user');
    },

    refresh: tryRefreshToken,

    me: async () => {
        const user = await apiFetch('/auth/me');
        localStorage.setItem('user', JSON.stringify(user));
        return user;
    },

    getCurrentUser: () => {
        const user = localStorage.getItem('user');
        return user ? JSON.parse(user) : null;
    },

    isAuthenticated: () => {
        return !!getAuthToken();
    },
};

// Lightweight password strength estimator (client-side hint only — server enforces)
export function passwordStrength(pw) {
    if (!pw) return { score: 0, label: '', color: 'var(--text-3)' };
    let score = 0;
    if (pw.length >= 8) score++;
    if (pw.length >= 12) score++;
    if (/[a-z]/.test(pw) && /[A-Z]/.test(pw)) score++;
    if (/\d/.test(pw)) score++;
    if (/[^A-Za-z0-9]/.test(pw)) score++;
    const map = [
        { label: 'Muy débil', color: '#ef4444' },
        { label: 'Débil',      color: '#f97316' },
        { label: 'Aceptable',  color: '#f59e0b' },
        { label: 'Buena',      color: '#22c55e' },
        { label: 'Excelente',  color: '#16a34a' }
    ];
    const idx = Math.max(0, Math.min(score - 1, 4));
    return { score, ...map[idx] };
}

// Contacts API
export const contactsAPI = {
    getAll: async (page = 1, search = '', perPage = 20) => {
        return apiFetch(`/contacts?page=${page}&search=${search}&per_page=${perPage}`);
    },

    getById: async (id) => {
        return apiFetch(`/contacts/${id}`);
    },

    create: async (contactData) => {
        return apiFetch('/contacts', {
            method: 'POST',
            body: JSON.stringify(contactData),
        });
    },

    update: async (id, contactData) => {
        return apiFetch(`/contacts/${id}`, {
            method: 'PUT',
            body: JSON.stringify(contactData),
        });
    },

    delete: async (id) => {
        return apiFetch(`/contacts/${id}`, {
            method: 'DELETE',
        });
    },

    importCsv: async (file) => {
        const fd = new FormData();
        fd.append('file', file);
        const token = getAuthToken();
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const resp = await fetch(`${API_BASE_URL}/contacts/import`, {
            method: 'POST',
            headers,
            body: fd
        });
        const data = await resp.json().catch(() => ({}));
        if (!resp.ok) throw new Error(data.error || 'Importación falló');
        return data;
    },

    importRows: async (rows) =>
        apiFetch('/contacts/import', {
            method: 'POST',
            body: JSON.stringify({ rows })
        }),
};

// Conversations API
export const conversationsAPI = {
    getAll: async (status = null, assignedToMe = false) => {
        let url = '/conversations?';
        if (status) url += `status=${status}&`;
        if (assignedToMe) url += 'assigned_to_me=true';
        return apiFetch(url);
    },

    getById: async (id) => {
        return apiFetch(`/conversations/${id}`);
    },

    markRead: async (id) => {
        return apiFetch(`/conversations/${id}/read`, { method: 'POST' });
    },

    create: async (conversationData) => {
        return apiFetch('/conversations', {
            method: 'POST',
            body: JSON.stringify(conversationData),
        });
    },

    update: async (id, conversationData) => {
        return apiFetch(`/conversations/${id}`, {
            method: 'PUT',
            body: JSON.stringify(conversationData),
        });
    },

    assign: async (id, userIds) => {
        return apiFetch(`/conversations/${id}/assign`, {
            method: 'POST',
            body: JSON.stringify({ user_ids: userIds }),
        });
    },

    sendMessage: async (conversationId, content, attachments = [], messageType = 'outgoing') => {
        return apiFetch(`/conversations/${conversationId}/messages`, {
            method: 'POST',
            body: JSON.stringify({ content, message_type: messageType, attachments }),
        });
    },
};

// Labels API
export const labelsAPI = {
    getAll: async () => {
        return apiFetch('/labels');
    },

    create: async (labelData) => {
        return apiFetch('/labels', {
            method: 'POST',
            body: JSON.stringify(labelData),
        });
    },

    update: async (id, labelData) => {
        return apiFetch(`/labels/${id}`, {
            method: 'PUT',
            body: JSON.stringify(labelData),
        });
    },

    delete: async (id) => {
        return apiFetch(`/labels/${id}`, { method: 'DELETE' });
    },

    addToConversation: async (conversationId, labelId) => {
        return apiFetch(`/conversations/${conversationId}/labels`, {
            method: 'POST',
            body: JSON.stringify({ label_id: labelId }),
        });
    },

    removeFromConversation: async (conversationId, labelId) => {
        return apiFetch(`/conversations/${conversationId}/labels/${labelId}`, {
            method: 'DELETE',
        });
    },

    addToContact: async (contactId, labelId) => {
        return apiFetch(`/contacts/${contactId}/labels`, {
            method: 'POST',
            body: JSON.stringify({ label_id: labelId }),
        });
    },

    removeFromContact: async (contactId, labelId) => {
        return apiFetch(`/contacts/${contactId}/labels/${labelId}`, {
            method: 'DELETE',
        });
    },
};

// Users API
export const usersAPI = {
    getAll: async () => {
        return apiFetch('/users');
    },

    getById: async (id) => {
        return apiFetch(`/users/${id}`);
    },

    update: async (id, userData) => {
        return apiFetch(`/users/${id}`, {
            method: 'PUT',
            body: JSON.stringify(userData),
        });
    },
};

// Stats API
export const statsAPI = {
    getDashboard: async () => {
        return apiFetch('/stats/dashboard');
    },
    // CRM-aware time-series + funnel for the Home charts
    getSeries: async (days = 14) => {
        return apiFetch(`/crm/stats/series?days=${days}`);
    },
};

// ============ Super Admin ============
export const superAdminAPI = {
    dashboard: () => apiFetch('/superadmin/dashboard'),

    // Organizations
    listOrgs: (q = '', status = '', plan = '') => {
        const qs = [];
        if (q) qs.push(`q=${encodeURIComponent(q)}`);
        if (status) qs.push(`status=${status}`);
        if (plan) qs.push(`plan=${plan}`);
        return apiFetch(`/superadmin/organizations${qs.length ? '?' + qs.join('&') : ''}`);
    },
    getOrg: (id) => apiFetch(`/superadmin/organizations/${id}`),
    createOrg: (data) => apiFetch('/superadmin/organizations', { method: 'POST', body: JSON.stringify(data) }),
    updateOrg: (id, data) => apiFetch(`/superadmin/organizations/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    suspendOrg: (id, reason) => apiFetch(`/superadmin/organizations/${id}/suspend`, { method: 'POST', body: JSON.stringify({ reason }) }),
    resumeOrg: (id) => apiFetch(`/superadmin/organizations/${id}/resume`, { method: 'POST' }),
    deleteOrg: (id) => apiFetch(`/superadmin/organizations/${id}`, { method: 'DELETE' }),
    impersonate: (orgId, userId = null) =>
        apiFetch(`/superadmin/organizations/${orgId}/impersonate`, {
            method: 'POST', body: JSON.stringify({ user_id: userId })
        }),
    applyPlan: (orgId, plan) =>
        apiFetch(`/superadmin/organizations/${orgId}/apply-plan`, {
            method: 'POST', body: JSON.stringify({ plan })
        }),

    // Plans
    listPlans: () => apiFetch('/superadmin/plans'),

    // AI billing
    billingOverview: (days = 30) => apiFetch(`/superadmin/billing/overview?days=${days}`),
    creditOrg: (orgId, data) =>
        apiFetch(`/superadmin/organizations/${orgId}/credit`, {
            method: 'POST', body: JSON.stringify(data)
        }),
    billingSettings: () => apiFetch('/superadmin/billing/settings'),
    saveBillingSettings: (data) =>
        apiFetch('/superadmin/billing/settings', { method: 'PUT', body: JSON.stringify(data) }),
    listPurchaseRequests: (status = '') =>
        apiFetch(`/superadmin/purchase-requests${status ? `?status=${status}` : ''}`),
    approvePurchaseRequest: (id, data = {}) =>
        apiFetch(`/superadmin/purchase-requests/${id}/approve`, { method: 'POST', body: JSON.stringify(data) }),
    rejectPurchaseRequest: (id, reviewNote = '') =>
        apiFetch(`/superadmin/purchase-requests/${id}/reject`, {
            method: 'POST', body: JSON.stringify({ review_note: reviewNote })
        }),
    listPlatformProviders: () => apiFetch('/superadmin/billing/providers'),
    createPlatformProvider: (data) =>
        apiFetch('/superadmin/billing/providers', { method: 'POST', body: JSON.stringify(data) }),
    updatePlatformProvider: (id, data) =>
        apiFetch(`/superadmin/billing/providers/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    deletePlatformProvider: (id) =>
        apiFetch(`/superadmin/billing/providers/${id}`, { method: 'DELETE' }),

    // Packages & modules
    listModules: () => apiFetch('/superadmin/modules'),
    listPackages: () => apiFetch('/superadmin/packages'),
    createPackage: (data) => apiFetch('/superadmin/packages', { method: 'POST', body: JSON.stringify(data) }),
    updatePackage: (id, data) => apiFetch(`/superadmin/packages/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    deletePackage: (id) => apiFetch(`/superadmin/packages/${id}`, { method: 'DELETE' }),
    assignPackage: (orgId, packageId) =>
        apiFetch(`/superadmin/organizations/${orgId}/assign-package`, {
            method: 'POST', body: JSON.stringify({ package_id: packageId })
        }),

    // Users (cross-org)
    listUsers: (q = '', orgId = null) => {
        const qs = [];
        if (q) qs.push(`q=${encodeURIComponent(q)}`);
        if (orgId) qs.push(`organization_id=${orgId}`);
        return apiFetch(`/superadmin/users${qs.length ? '?' + qs.join('&') : ''}`);
    },
    updateUser: (id, data) => apiFetch(`/superadmin/users/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    resetPassword: (id, newPassword) =>
        apiFetch(`/superadmin/users/${id}/reset-password`, {
            method: 'POST', body: JSON.stringify({ new_password: newPassword })
        }),

    // Audit
    audit: (page = 1, filters = {}) => {
        const qs = [`page=${page}`];
        if (filters.action) qs.push(`action=${encodeURIComponent(filters.action)}`);
        if (filters.actor_id) qs.push(`actor_id=${filters.actor_id}`);
        if (filters.organization_id) qs.push(`organization_id=${filters.organization_id}`);
        return apiFetch(`/superadmin/audit?${qs.join('&')}`);
    }
};

// ============ Campaigns (WhatsApp broadcasts) ============
export const campaignsAPI = {
    list: (page = 1) => apiFetch(`/campaigns?page=${page}`),
    get: (id) => apiFetch(`/campaigns/${id}`),
    create: (data) => apiFetch('/campaigns', { method: 'POST', body: JSON.stringify(data) }),
    update: (id, data) => apiFetch(`/campaigns/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    remove: (id) => apiFetch(`/campaigns/${id}`, { method: 'DELETE' }),
    estimate: (audience) => apiFetch('/campaigns/estimate', { method: 'POST', body: JSON.stringify({ audience }) }),
    schedule: (id, scheduledAt = null) =>
        apiFetch(`/campaigns/${id}/schedule`, {
            method: 'POST', body: JSON.stringify(scheduledAt ? { scheduled_at: scheduledAt } : {})
        }),
    cancel: (id) => apiFetch(`/campaigns/${id}/cancel`, { method: 'POST' }),
    recipients: (id, page = 1, status = '') =>
        apiFetch(`/campaigns/${id}/recipients?page=${page}${status ? `&status=${status}` : ''}`),
    templates: (channelId) => apiFetch(`/campaigns/templates?channel_id=${channelId}`)
};

// ============ Onboarding ============
export const onboardingAPI = {
    status: () => apiFetch('/onboarding/status'),
    dismiss: () => apiFetch('/onboarding/dismiss', { method: 'POST' })
};

// ============ Billing (créditos IA, org-facing) ============
export const billingAPI = {
    balance: () => apiFetch('/billing/balance'),
    transactions: (page = 1, kind = '') =>
        apiFetch(`/billing/transactions?page=${page}${kind ? `&kind=${kind}` : ''}`),
    usage: (days = 30) => apiFetch(`/billing/usage?days=${days}`),
    // SINPE: payment info + package/top-up requests
    paymentInfo: () => apiFetch('/billing/payment-info'),
    requests: () => apiFetch('/billing/requests'),
    createRequest: (data) => apiFetch('/billing/requests', { method: 'POST', body: JSON.stringify(data) })
};

// ============ Data portability (export/import) ============
export const dataAPI = {
    exportContacts: (fmt = 'csv') => {
        const stamp = new Date().toISOString().slice(0, 10);
        return apiDownload(`/contacts/export?format=${fmt}`, `contactos-${stamp}.${fmt}`);
    },
    exportConversations: (from = '', to = '') => {
        const qs = [];
        if (from) qs.push(`from=${from}`);
        if (to) qs.push(`to=${to}`);
        const stamp = new Date().toISOString().slice(0, 10);
        return apiDownload(
            `/conversations/export${qs.length ? '?' + qs.join('&') : ''}`,
            `conversaciones-${stamp}.json`
        );
    },
    importConversations: async (file) => {
        const fd = new FormData();
        fd.append('file', file);
        const token = getAuthToken();
        const resp = await fetch(`${API_BASE_URL}/conversations/import`, {
            method: 'POST',
            headers: token ? { Authorization: `Bearer ${token}` } : {},
            body: fd
        });
        const data = await resp.json().catch(() => ({}));
        if (!resp.ok) throw new Error(data.error || 'Importación falló');
        return data;
    }
};

// ============ Email ============
export const emailAPI = {
    status: () => apiFetch('/email/status'),
    test: (to) => apiFetch('/email/test', { method: 'POST', body: JSON.stringify({ to }) })
};

// ============ Salesbot Flows ============
export const flowsAPI = {
    list: () => apiFetch('/flows'),
    get: (id) => apiFetch(`/flows/${id}`),
    create: (data) => apiFetch('/flows', { method: 'POST', body: JSON.stringify(data) }),
    update: (id, data) => apiFetch(`/flows/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    remove: (id) => apiFetch(`/flows/${id}`, { method: 'DELETE' }),
    duplicate: (id) => apiFetch(`/flows/${id}/duplicate`, { method: 'POST' }),
    execute: (id, opts = {}) =>
        apiFetch(`/flows/${id}/execute`, { method: 'POST', body: JSON.stringify(opts) }),
    resume: (executionId, input) =>
        apiFetch(`/flows/executions/${executionId}/resume`, {
            method: 'POST', body: JSON.stringify({ input })
        }),
    validate: (id, data = {}) =>
        apiFetch(`/flows/${id}/validate`, { method: 'POST', body: JSON.stringify(data) }),
    stats: (id) => apiFetch(`/flows/${id}/stats`),
    listExecutions: (flowId) => apiFetch(`/flows/${flowId}/executions`)
};

// ============ Global search ============
export const searchAPI = {
    query: (q) => apiFetch(`/search?q=${encodeURIComponent(q)}`)
};

// ============ Team & Collaboration ============
export const teamAPI = {
    // Teams
    listTeams: () => apiFetch('/team/teams'),
    createTeam: (data) => apiFetch('/team/teams', { method: 'POST', body: JSON.stringify(data) }),
    updateTeam: (id, data) => apiFetch(`/team/teams/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    deleteTeam: (id) => apiFetch(`/team/teams/${id}`, { method: 'DELETE' }),
    addMember: (teamId, userId) =>
        apiFetch(`/team/teams/${teamId}/members`, { method: 'POST', body: JSON.stringify({ user_id: userId }) }),
    removeMember: (teamId, userId) =>
        apiFetch(`/team/teams/${teamId}/members/${userId}`, { method: 'DELETE' }),

    // Presence
    getPresence: () => apiFetch('/team/presence'),
    setMyPresence: (status, message = null, availableForHandoff = null) => {
        const body = { status };
        if (message !== null) body.message = message;
        if (availableForHandoff !== null) body.available_for_handoff = availableForHandoff;
        return apiFetch('/team/presence/me', { method: 'PUT', body: JSON.stringify(body) });
    },
    heartbeat: () => apiFetch('/team/presence/heartbeat', { method: 'POST' }),

    // Routing
    getQueue: () => apiFetch('/team/queue'),
    claim: (convId) => apiFetch(`/team/conversations/${convId}/claim`, { method: 'POST' }),
    transfer: (convId, opts) =>
        apiFetch(`/team/conversations/${convId}/transfer`, {
            method: 'POST',
            body: JSON.stringify(opts)
        }),
    releaseAI: (convId) => apiFetch(`/team/conversations/${convId}/release-ai`, { method: 'POST' }),

    // Mentions
    listMentions: (unreadOnly = false) =>
        apiFetch(`/team/mentions${unreadOnly ? '?unread=1' : ''}`),
    markMentionRead: (id) => apiFetch(`/team/mentions/${id}/read`, { method: 'PUT' }),
    markAllMentionsRead: () => apiFetch('/team/mentions/read-all', { method: 'POST' }),

    // Stats
    getStats: (days = 14) => apiFetch(`/team/stats?days=${days}`)
};

// ============ AI Agents ============
export const agentsAPI = {
    listTemplates: () => apiFetch('/agents/templates'),
    listTools: () => apiFetch('/agents/tools'),

    list: () => apiFetch('/agents'),
    get: (id) => apiFetch(`/agents/${id}`),
    create: (data) => apiFetch('/agents', { method: 'POST', body: JSON.stringify(data) }),
    update: (id, data) => apiFetch(`/agents/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    remove: (id) => apiFetch(`/agents/${id}`, { method: 'DELETE' }),
    duplicate: (id) => apiFetch(`/agents/${id}/duplicate`, { method: 'POST' }),

    test: (id, message, history = [], opts = {}) =>
        apiFetch(`/agents/${id}/test`, {
            method: 'POST',
            body: JSON.stringify({ message, history, ...opts })
        }),

    handoff: (agentId, conversationId, opts = {}) =>
        apiFetch(`/agents/${agentId}/handoff`, {
            method: 'POST',
            body: JSON.stringify({ conversation_id: conversationId, ...opts })
        }),

    interactions: (id, page = 1, rating = null) => {
        const qs = [`page=${page}`];
        if (rating !== null && rating !== undefined && rating !== '') qs.push(`rating=${rating}`);
        return apiFetch(`/agents/${id}/interactions?${qs.join('&')}`);
    },

    rateInteraction: (interactionId, rating, note = '', markExemplar = false) =>
        apiFetch(`/agents/interactions/${interactionId}/rate`, {
            method: 'POST',
            body: JSON.stringify({ rating, note, mark_exemplar: markExemplar })
        }),

    analytics: (id, days = 14) => apiFetch(`/agents/${id}/analytics?days=${days}`),
    statsSeries: (days = 14) => apiFetch(`/agents/stats/series?days=${days}`)
};

// ============ CRM (Pipelines, Stages, Leads) ============
export const crmAPI = {
    // Pipelines
    getPipelines: () => apiFetch('/crm/pipelines'),
    createPipeline: (data) => apiFetch('/crm/pipelines', { method: 'POST', body: JSON.stringify(data) }),
    updatePipeline: (id, data) => apiFetch(`/crm/pipelines/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    deletePipeline: (id) => apiFetch(`/crm/pipelines/${id}`, { method: 'DELETE' }),

    // Stages
    createStage: (pipelineId, data) =>
        apiFetch(`/crm/pipelines/${pipelineId}/stages`, { method: 'POST', body: JSON.stringify(data) }),
    updateStage: (id, data) => apiFetch(`/crm/stages/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    deleteStage: (id) => apiFetch(`/crm/stages/${id}`, { method: 'DELETE' }),

    // Leads
    listLeads: (pipelineId = null, status = null, contactId = null) => {
        const qs = [];
        if (pipelineId) qs.push(`pipeline_id=${pipelineId}`);
        if (status) qs.push(`status=${status}`);
        if (contactId) qs.push(`contact_id=${contactId}`);
        return apiFetch(`/crm/leads${qs.length ? '?' + qs.join('&') : ''}`);
    },
    getLead: (id) => apiFetch(`/crm/leads/${id}`),
    createLead: (data) => apiFetch('/crm/leads', { method: 'POST', body: JSON.stringify(data) }),
    updateLead: (id, data) => apiFetch(`/crm/leads/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    moveLead: (id, stageId, position = null) =>
        apiFetch(`/crm/leads/${id}/move`, {
            method: 'PUT',
            body: JSON.stringify({ stage_id: stageId, position })
        }),
    deleteLead: (id) => apiFetch(`/crm/leads/${id}`, { method: 'DELETE' }),

    // Bulk operations on leads
    bulkLeads: (ids, action, payload = {}) =>
        apiFetch('/crm/leads/bulk', {
            method: 'POST',
            body: JSON.stringify({ ids, action, payload })
        }),

    // Activities / timeline
    listActivities: (leadId) => apiFetch(`/crm/leads/${leadId}/activities`),
    createActivity: (leadId, data) =>
        apiFetch(`/crm/leads/${leadId}/activities`, { method: 'POST', body: JSON.stringify(data) }),
    deleteActivity: (leadId, activityId) =>
        apiFetch(`/crm/leads/${leadId}/activities/${activityId}`, { method: 'DELETE' }),

    // Create lead from a conversation
    createLeadFromConversation: (conversationId, data = {}) =>
        apiFetch(`/crm/leads/from-conversation/${conversationId}`, {
            method: 'POST',
            body: JSON.stringify(data)
        })
};

// Canned Responses API
export const cannedResponsesAPI = {
    getAll: async () => {
        return apiFetch('/canned-responses');
    },

    create: async (responseData) => {
        return apiFetch('/canned-responses', {
            method: 'POST',
            body: JSON.stringify(responseData),
        });
    },
};

// ============ AI & AUTOMATION APIs ============

// AI Providers API
export const aiProvidersAPI = {
    getAll: async () => {
        return apiFetch('/ai/providers');
    },

    getById: async (id) => {
        return apiFetch(`/ai/providers/${id}`);
    },

    create: async (providerData) => {
        return apiFetch('/ai/providers', {
            method: 'POST',
            body: JSON.stringify(providerData),
        });
    },

    update: async (id, providerData) => {
        return apiFetch(`/ai/providers/${id}`, {
            method: 'PUT',
            body: JSON.stringify(providerData),
        });
    },

    delete: async (id) => {
        return apiFetch(`/ai/providers/${id}`, {
            method: 'DELETE',
        });
    },

    // Get available models with pricing
    getModels: async (providerType = null) => {
        const url = providerType ? `/ai/models/${providerType}` : '/ai/models';
        return apiFetch(url);
    },
};

// Knowledge Base API
export const knowledgeBaseAPI = {
    getAll: async (params = {}) => {
        let url = '/ai/knowledge?';
        if (params.category) url += `category=${encodeURIComponent(params.category)}&`;
        if (params.search) url += `search=${encodeURIComponent(params.search)}`;
        return apiFetch(url);
    },

    create: async (entryData) => {
        return apiFetch('/ai/knowledge', {
            method: 'POST',
            body: JSON.stringify(entryData),
        });
    },

    update: async (id, entryData) => {
        return apiFetch(`/ai/knowledge/${id}`, {
            method: 'PUT',
            body: JSON.stringify(entryData),
        });
    },

    delete: async (id) => {
        return apiFetch(`/ai/knowledge/${id}`, {
            method: 'DELETE',
        });
    },

    reindex: async () => apiFetch('/ai/knowledge/reindex', { method: 'POST' }),
    indexPending: async (limit = 50) =>
        apiFetch(`/ai/knowledge/index-pending?limit=${limit}`, { method: 'POST' })
};

// Chatbot Configs API
export const chatbotAPI = {
    getAll: async () => {
        return apiFetch('/ai/chatbots');
    },

    getById: async (id) => {
        return apiFetch(`/ai/chatbots/${id}`);
    },

    create: async (chatbotData) => {
        return apiFetch('/ai/chatbots', {
            method: 'POST',
            body: JSON.stringify(chatbotData),
        });
    },

    update: async (id, chatbotData) => {
        return apiFetch(`/ai/chatbots/${id}`, {
            method: 'PUT',
            body: JSON.stringify(chatbotData),
        });
    },

    // Enable AI for a conversation
    enableForConversation: async (conversationId, chatbotConfigId) => {
        return apiFetch(`/ai/conversations/${conversationId}/enable`, {
            method: 'POST',
            body: JSON.stringify({ chatbot_config_id: chatbotConfigId }),
        });
    },

    // Disable AI for a conversation
    disableForConversation: async (conversationId) => {
        return apiFetch(`/ai/conversations/${conversationId}/disable`, {
            method: 'POST',
        });
    },

    // Get AI status for conversation
    getConversationStatus: async (conversationId) => {
        return apiFetch(`/ai/conversations/${conversationId}/ai-status`);
    },

    // Generate AI response
    generateResponse: async (conversationId, chatbotConfigId, message) => {
        return apiFetch('/ai/generate', {
            method: 'POST',
            body: JSON.stringify({
                conversation_id: conversationId,
                chatbot_config_id: chatbotConfigId,
                message: message
            }),
        });
    },
};

// Automations API
export const automationsAPI = {
    getAll: async () => {
        return apiFetch('/ai/automations');
    },

    create: async (automationData) => {
        return apiFetch('/ai/automations', {
            method: 'POST',
            body: JSON.stringify(automationData),
        });
    },

    update: async (id, automationData) => {
        return apiFetch(`/ai/automations/${id}`, {
            method: 'PUT',
            body: JSON.stringify(automationData),
        });
    },

    delete: async (id) => {
        return apiFetch(`/ai/automations/${id}`, {
            method: 'DELETE',
        });
    },
};

// ============ CHANNELS & INTEGRATIONS APIs ============

// Channels API
export const channelsAPI = {
    getAll: async () => {
        return apiFetch('/channels');
    },

    getById: async (id) => {
        return apiFetch(`/channels/${id}`);
    },

    create: async (channelData) => {
        return apiFetch('/channels', {
            method: 'POST',
            body: JSON.stringify(channelData),
        });
    },

    update: async (id, channelData) => {
        return apiFetch(`/channels/${id}`, {
            method: 'PUT',
            body: JSON.stringify(channelData),
        });
    },

    delete: async (id) => {
        return apiFetch(`/channels/${id}`, {
            method: 'DELETE',
        });
    },

    // Integration management
    getIntegration: async (channelId) => {
        return apiFetch(`/channels/${channelId}/integration`);
    },

    createIntegration: async (channelId, integrationData) => {
        return apiFetch(`/channels/${channelId}/integration`, {
            method: 'POST',
            body: JSON.stringify(integrationData),
        });
    },

    deleteIntegration: async (channelId) => {
        return apiFetch(`/channels/${channelId}/integration`, {
            method: 'DELETE',
        });
    },

    testIntegration: async (channelId) => {
        return apiFetch(`/channels/${channelId}/test`, {
            method: 'POST',
        });
    },

    // Widget config (public)
    getWidgetConfig: async (channelId) => {
        return apiFetch(`/channels/${channelId}/widget-config`);
    },

    // Webhook events
    getWebhookEvents: async (channelId, page = 1, processed = null) => {
        let url = `/channels/${channelId}/webhook-events?page=${page}`;
        if (processed !== null) url += `&processed=${processed}`;
        return apiFetch(url);
    },

    retryWebhookEvent: async (eventId) => {
        return apiFetch(`/channels/webhook-events/${eventId}/retry`, {
            method: 'POST',
        });
    },
};

export const organizationsAPI = {
    getAll: async () => {
        return apiFetch('/organizations');
    },

    getById: async (orgId) => {
        return apiFetch(`/organizations/${orgId}`);
    },

    create: async (data) => {
        return apiFetch('/organizations', {
            method: 'POST',
            body: JSON.stringify(data),
        });
    },

    update: async (orgId, data) => {
        return apiFetch(`/organizations/${orgId}`, {
            method: 'PUT',
            body: JSON.stringify(data),
        });
    },

    delete: async (orgId) => {
        return apiFetch(`/organizations/${orgId}`, {
            method: 'DELETE',
        });
    },

    getUsers: async (orgId) => {
        return apiFetch(`/organizations/${orgId}/users`);
    },

    createUser: async (orgId, data) => {
        return apiFetch(`/organizations/${orgId}/users`, {
            method: 'POST',
            body: JSON.stringify(data),
        });
    },

    getStats: async (orgId) => {
        return apiFetch(`/organizations/${orgId}/stats`);
    },
};
